/* Role honesty (Tier-1 C1, 2026-09-04): a verb the current role cannot
 * perform is rendered DISABLED with the rule text BEFORE the click — never
 * a live button that 403s.
 *
 * This table MIRRORS the ranks the backend enforces today; the backend is
 * the rule, this is its honest rendering. Every entry names the guard it
 * mirrors so a rank change on the server is a one-line flip here:
 *
 *   reco_book_plug            rk/decks book-plug           require_role("ops_officer")
 *   reco_confirm_pre_existing implied_trade_request_handler._PRE_EXISTING_MIN_ROLE = ops_officer (D4 2026-09-03)
 *   reco_resolve_break        recon/break_resolution._MIN_ROLE = ops_officer
 *   waive_obligation          PATCH /evidence-status/{id}/waive   require_role("ops_officer") (Ale 2026-07-08)
 *   waive_gap                 InvestigationService._WAIVE_MIN_ROLES = {manager, admin}
 *   evidence_signoff          POST /tasks/{id}/resolve (evidence_approval)  any ops_officer
 *
 * Pure module (no React, no DOM): unit-tested under tests/unit. */

export type Role = 'ops_officer' | 'manager' | 'admin'

export const ROLE_RANK: Record<Role, number> = { ops_officer: 1, manager: 2, admin: 3 }

export type Verb =
  | 'reco_book_plug'
  | 'reco_confirm_pre_existing'
  | 'reco_resolve_break'
  | 'waive_obligation'
  | 'waive_gap'
  | 'evidence_signoff'

/** Minimum role per verb — the CURRENT backend rules (see header). */
export const VERB_MIN_ROLE: Record<Verb, Role> = {
  reco_book_plug: 'ops_officer',
  reco_confirm_pre_existing: 'ops_officer',
  reco_resolve_break: 'ops_officer',
  waive_obligation: 'ops_officer',
  waive_gap: 'manager',
  evidence_signoff: 'ops_officer',
}

/** What the officer reads next to a verb their role cannot perform. */
const RULE_TEXT: Record<Verb, string> = {
  reco_book_plug: 'manager confirms — four-eyes',
  reco_confirm_pre_existing: 'manager confirms — four-eyes',
  reco_resolve_break: 'manager confirms — four-eyes',
  waive_obligation: 'manager waives — four-eyes',
  waive_gap: 'manager waives — four-eyes',
  evidence_signoff: 'manager verifies — four-eyes',
}

/** The self-verification refusal (four-eyes on the verify card): the
 * officer who booked the object may not verify the document proving it.
 * Same words as the server's 403. */
export const SELF_VERIFICATION_RULE = 'you booked this — a second officer must verify'

export interface VerbRule {
  allowed: boolean
  /** Rule text to render when not allowed; null when allowed. */
  rule: string | null
}

export function verbRule(verb: Verb, role: Role | null | undefined): VerbRule {
  const have = role ? (ROLE_RANK[role] ?? 0) : 0
  const need = ROLE_RANK[VERB_MIN_ROLE[verb]]
  return have >= need ? { allowed: true, rule: null } : { allowed: false, rule: RULE_TEXT[verb] }
}

/** Officer-facing role word for the small role tag. */
export function roleLabel(role: Role | null | undefined): string {
  switch (role) {
    case 'manager':
      return 'manager'
    case 'admin':
      return 'admin'
    default:
      return 'ops officer'
  }
}
